import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOrgs } from '@/contexts/OrgsContext';
import { useGetOrgInvoices } from '@/api/auth/billing/billing';
import type { InvoiceItem } from '@/api/auth/missionImpactAuthAPI.schemas';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

const PAGE_SIZE = 10;

/**
 * Displays a paginated table of invoice history for the organization.
 */
export function InvoiceHistoryTable() {
  const { t, language } = useLanguage();
  const { selectedOrganizationId } = useOrgs();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading } = useGetOrgInvoices(
    selectedOrganizationId ?? '',
    { page: currentPage, pageSize: PAGE_SIZE },
    {
      query: {
        enabled: !!selectedOrganizationId,
      },
    }
  );

  const invoices = data?.data.items ?? [];
  const hasMore = data?.data.hasMore ?? false;
  const totalCount = data?.data.totalCount ?? 0;

  const locale = useMemo(() => {
    switch (language) {
      case 'fr':
        return 'fr-FR';
      case 'de':
        return 'de-DE';
      case 'es':
        return 'es-ES';
      default:
        return 'en-US';
    }
  }, [language]);

  /**
   * Format a date string to a localized date
   */
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Format an amount with currency
   */
  const formatAmount = (amount: number | undefined, currency: string | undefined) => {
    if (amount === undefined) return '-';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency ?? 'USD',
    }).format(amount);
  };

  /**
   * Get status badge variant based on invoice status
   */
  const getStatusBadgeVariant = (status: string | undefined) => {
    switch (status) {
      case 'Paid':
        return 'default' as const;
      case 'Open':
        return 'secondary' as const;
      case 'Uncollectible':
      case 'Void':
        return 'destructive' as const;
      default: // Draft or unknown
        return 'outline' as const;
    }
  };

  /**
   * Get status label based on invoice status
   */
  const getStatusLabel = (status: string | undefined) => {
    switch (status) {
      case 'Paid':
        return t.billing.invoiceHistory.status.paid;
      case 'Open':
        return t.billing.invoiceHistory.status.open;
      case 'Draft':
        return t.billing.invoiceHistory.status.draft;
      case 'Uncollectible':
        return t.billing.invoiceHistory.status.uncollectible;
      case 'Void':
        return t.billing.invoiceHistory.status.void;
      default:
        return t.billing.invoiceHistory.status.unknown;
    }
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  /**
   * Render skeleton rows for loading state
   */
  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${String(index)}`}>
        <TableCell>
          <Skeleton className="h-4 w-24" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-20" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-5 w-16" />
        </TableCell>
        <TableCell>
          <div className="flex gap-2">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  /**
   * Render invoice rows
   */
  const renderInvoiceRows = (items: InvoiceItem[]) => {
    return items.map((invoice) => (
      <TableRow key={invoice.invoiceId}>
        <TableCell className="text-muted-foreground">{formatDate(invoice.createdAt)}</TableCell>
        <TableCell className="font-medium">
          {formatAmount(invoice.amount, invoice.currency)}
        </TableCell>
        <TableCell>
          <Badge variant={getStatusBadgeVariant(invoice.status)}>{getStatusLabel(invoice.status)}</Badge>
        </TableCell>
        <TableCell>
          {invoice.hostedInvoiceUrl && (
            <Button variant="ghost" size="sm" asChild>
              <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1" />
                {t.billing.invoiceHistory.view}
              </a>
            </Button>
          )}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.billing.invoiceHistory.title}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.billing.invoiceHistory.date}</TableHead>
                <TableHead>{t.billing.invoiceHistory.amount}</TableHead>
                <TableHead>{t.billing.invoiceHistory.statusLabel}</TableHead>
                <TableHead>{t.billing.invoiceHistory.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{renderSkeletonRows()}</TableBody>
          </Table>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {t.billing.invoiceHistory.noInvoices}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.billing.invoiceHistory.date}</TableHead>
                  <TableHead>{t.billing.invoiceHistory.amount}</TableHead>
                  <TableHead>{t.billing.invoiceHistory.statusLabel}</TableHead>
                  <TableHead>{t.billing.invoiceHistory.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderInvoiceRows(invoices)}</TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <div className="text-sm text-muted-foreground">
                {t.billing.invoiceHistory.showing
                  .replace('{count}', String(invoices.length))
                  .replace('{total}', String(totalCount))}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {t.billing.invoiceHistory.previous}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!hasMore}
                >
                  {t.billing.invoiceHistory.next}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
